/**
 * My Lists Service
 * User-created custom lists. The one-tap Watchlist lives in watchlistService.js.
 * All endpoints require an authenticated user; the backend enforces the
 * list / item limits and returns machine-readable codes (see MY_LIST_ERROR_CODES).
 */

import { apiRequest, API_BASE_URL } from './api';

export const MY_LIST_ERROR_CODES = Object.freeze({
    LIST_NOT_FOUND: 'list_not_found',
    LIST_ITEM_NOT_FOUND: 'list_item_not_found',
    LIST_LIMIT_REACHED: 'list_limit_reached',
    LIST_ITEM_LIMIT_REACHED: 'list_item_limit_reached',
    DUPLICATE_LIST_ITEM: 'duplicate_list_item',
    INVALID_LIST_NAME: 'invalid_list_name',
    INVALID_LIST_VISIBILITY: 'invalid_list_visibility',
});

/** Friendly copy for a My Lists API error. Limit errors keep the backend's message. */
export function getMyListErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
    if (!err) return fallback;
    switch (err.code) {
        case MY_LIST_ERROR_CODES.LIST_LIMIT_REACHED:
        case MY_LIST_ERROR_CODES.LIST_ITEM_LIMIT_REACHED:
        case MY_LIST_ERROR_CODES.DUPLICATE_LIST_ITEM:
        case MY_LIST_ERROR_CODES.INVALID_LIST_NAME:
            return err.message;
        case MY_LIST_ERROR_CODES.LIST_NOT_FOUND:
            return 'This list no longer exists.';
        case MY_LIST_ERROR_CODES.LIST_ITEM_NOT_FOUND:
            return 'That title is no longer in this list.';
        default:
            return err.message || fallback;
    }
}

export const MyListsService = {
    /** → { lists: UserListSummary[] } (summaries only, never full contents) */
    list() {
        return apiRequest('/lists');
    },

    /** → { list: { ...summary, progress, items } } */
    get(listId) {
        return apiRequest(`/lists/${listId}`);
    },

    create({ name, description, visibility }) {
        return apiRequest('/lists', { method: 'POST', body: { name, description, ...(visibility ? { visibility } : {}) } });
    },

    update(listId, { name, description, coverImageUrl, visibility }) {
        return apiRequest(`/lists/${listId}`, { method: 'PATCH', body: { name, description, coverImageUrl, visibility } });
    },

    /** 'private' turns the share link off; 'public' turns it on and mints a slug once. */
    setVisibility(listId, visibility) {
        return apiRequest(`/lists/${listId}`, { method: 'PATCH', body: { visibility } });
    },

    /** Anyone-with-the-link view. → { isPrivate } or { list, owner, items, shareUrl, cardUrl } */
    getShared(slug) {
        return apiRequest(`/share/list/${encodeURIComponent(slug)}`, { authenticated: false });
    },

    /** PNG share card served by the API (also the link's social preview image). */
    cardUrl(slug) {
        return `${API_BASE_URL}/share/list/${encodeURIComponent(slug)}/card.png`;
    },

    remove(listId) {
        return apiRequest(`/lists/${listId}`, { method: 'DELETE' });
    },

    /** seasonNumber null → movie / whole series; integer → exact TV season */
    addItem(listId, { mediaId, mediaType, seasonNumber = null }) {
        return apiRequest(`/lists/${listId}/items`, { method: 'POST', body: { mediaId, mediaType, seasonNumber } });
    },

    removeItem(listId, itemId) {
        return apiRequest(`/lists/${listId}/items/${itemId}`, { method: 'DELETE' });
    },

    reorder(listId, orderedItemIds) {
        return apiRequest(`/lists/${listId}/items/reorder`, { method: 'PATCH', body: { orderedItemIds } });
    },

    getMembership({ mediaId, mediaType, seasonNumber = null }) {
        return apiRequest('/lists/membership', {
            params: { mediaId, mediaType, ...(seasonNumber != null ? { seasonNumber } : {}) },
        });
    },

    setMembership({ mediaId, mediaType, seasonNumber = null, listIds, inWatchlist }) {
        return apiRequest('/lists/membership', {
            method: 'POST',
            body: { mediaId, mediaType, seasonNumber, listIds, ...(typeof inWatchlist === 'boolean' ? { inWatchlist } : {}) },
        });
    },
};
